import React, { useLayoutEffect, useRef, useState } from 'react';
import { Textfit, type TextfitProps } from 'react-textfit';

/**
 * `Textfit` that waits for its own slot to exist.
 *
 * react-textfit measures its root element in `componentDidMount` and gives up —
 * logging "Can not process element without height. Make sure the element is
 * displayed and has a static height." — whenever that measurement comes back 0.
 * That is the case for anything mounted while an ancestor is still collapsed or
 * hidden, which on a paywall is the normal first frame: the card is in the tree
 * before the products, the fonts and the section around it have settled.
 *
 * The placeholder below carries the same class and the same text, so measuring
 * it measures exactly the box Textfit is about to get. Only once that box has a
 * size is the slot handed over — and since the placeholder renders the text at
 * its CSS size, which is what an unfitted Textfit shows anyway, nothing moves.
 */
export const FitText: React.FC<TextfitProps> = ({
  className,
  children,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [measurable, setMeasurable] = useState(false);

  useLayoutEffect(() => {
    if (measurable) {
      return undefined;
    }

    const el = ref.current;

    if (!el) {
      return undefined;
    }

    const hasBox = () => el.clientHeight > 0 && el.clientWidth > 0;

    if (hasBox()) {
      setMeasurable(true);
      return undefined;
    }

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    // Fires as soon as the element gets a size — including when an ancestor
    // stops being hidden long after this mounted.
    const observer = new ResizeObserver(() => {
      if (hasBox()) {
        setMeasurable(true);
      }
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [measurable]);

  if (!measurable) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  return (
    <Textfit className={className} {...rest}>
      {children}
    </Textfit>
  );
};

export default FitText;
